import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, MouseEventHandler } from 'react';
import { MouseTooltip } from '@components/Tooltip';

type TooltipPosition = { x: number; y: number };

export type MouseTooltipHandlers = {
  onMouseEnter: MouseEventHandler<HTMLElement>;
  onMouseMove: MouseEventHandler<HTMLElement>;
  onMouseLeave: MouseEventHandler<HTMLElement>;
};

export function getMouseTooltipTriggerProps(
  label: string,
  handlers: MouseTooltipHandlers,
  shortcut?: string,
) {
  const tooltipText = shortcut ? `${label} [${shortcut}]` : label;
  return {
    'data-name': tooltipText,
    'data-tooltip-always': 'true',
    onMouseEnter: handlers.onMouseEnter,
    onMouseMove: handlers.onMouseMove,
    onMouseLeave: handlers.onMouseLeave,
  };
}

export function useTextTooltip() {
  const isActiveRef = useRef(false);
  const activeTargetRef = useRef<HTMLElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ x: 0, y: 0 });

  const hideTooltip = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    isActiveRef.current = false;
    activeTargetRef.current = null;
    setShowTooltip(false);
    setTooltipText('');
  }, []);

  useEffect(() => {
    if (!showTooltip) return undefined;

    const hideIfPointerLeavesTarget = (event: globalThis.PointerEvent) => {
      if (frameRef.current !== null) {
        return;
      }

      const { clientX, clientY } = event;

      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;

        const target = activeTargetRef.current;

        if (!target?.isConnected) {
          hideTooltip();
          return;
        }

        const rect = target.getBoundingClientRect();
        const isInside =
          clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom;

        if (!isInside) {
          hideTooltip();
        }
      });
    };

    const handleWindowMouseOut = (event: globalThis.MouseEvent) => {
      if (!event.relatedTarget) {
        hideTooltip();
      }
    };

    window.addEventListener('pointermove', hideIfPointerLeavesTarget);
    window.addEventListener('mouseout', handleWindowMouseOut);

    return () => {
      window.removeEventListener('pointermove', hideIfPointerLeavesTarget);
      window.removeEventListener('mouseout', handleWindowMouseOut);
      hideTooltip();
    };
  }, [hideTooltip, showTooltip]);

  const showTooltipAt = useCallback(
    (text: string, event: MouseEvent<HTMLElement>) => {
      isActiveRef.current = true;
      activeTargetRef.current = event.currentTarget;
      setTooltipText(text);
      setTooltipPos({ x: event.clientX, y: event.clientY });
      setShowTooltip(true);
    },
    [],
  );

  const handleTextMouseEnter = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const element = event.currentTarget;
      const isEllipsized = element.scrollWidth > element.clientWidth;
      const alwaysShow = element.dataset.tooltipAlways === 'true';

      const text = (element.dataset.name ?? '')
        .trim()
        .replace(/\s*,\s*/g, ',\n');

      if ((!isEllipsized && !alwaysShow) || !text) {
        hideTooltip();
        return;
      }

      showTooltipAt(text, event);
    },
    [hideTooltip, showTooltipAt],
  );

  const handleTextMouseMove = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!isActiveRef.current) return;
    setTooltipPos({ x: event.clientX, y: event.clientY });
  }, []);

  const handleTextMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  const tooltipHandlers = useMemo(
    () => ({
      onMouseEnter: handleTextMouseEnter,
      onMouseMove: handleTextMouseMove,
      onMouseLeave: handleTextMouseLeave,
    }),
    [handleTextMouseEnter, handleTextMouseLeave, handleTextMouseMove],
  );


  const renderTooltipText = (text: string) => {
    const match = text.match(/^(.*)\[([^\]]+)\]$/);
    if (!match) {
      return text;
    }

    const [, label, shortcut] = match;

    return (
      <>
        {label}(
        <span
          style={{
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          }}
        >
          {shortcut}
        </span>
        )
      </>
    );
  };

  const tooltipNode =
    showTooltip && tooltipText ? (
      <MouseTooltip x={tooltipPos.x} y={tooltipPos.y}>
        <span style={{ whiteSpace: 'pre-line' }}>
          {renderTooltipText(tooltipText)}
        </span>
      </MouseTooltip>
    ) : null;

  return {
    tooltipNode,
    tooltipHandlers,
    handleTextMouseEnter,
    handleTextMouseMove,
    handleTextMouseLeave,
  };
}
